import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from 'utils/shadcn';

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-sm text-sm font-medium transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground shadow hover:bg-primary/90',
        destructive:
          'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90',
        outline:
          'w-full h-[2.5625rem] bg-white hover:bg-[#FAFAFA] px-6 py-3 border border-[#A2A9B0] rounded-lg text-[#21272A] text-sm font-medium leading-[1.05rem]',
        dashed:
          'border py-1 border-dashed border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground text-xs',
        primary:
          'w-full h-[2.5625rem] bg-[#5B73F1] hover:bg-[#4360EF] rounded-lg px-6 py-3 text-white text-sm font-medium leading-[1.05rem]',
        secondary:
          'w-full h-[2.5625rem] bg-[#C1C7CD] hover:bg-[#C1C7CD90] rounded-lg px-6 py-3 text-[#21272A] text-sm font-medium leading-[1.05rem]',
        muted:
          'h-[1.375rem] bg-[#697077] hover:bg-[#54595f] rounded px-2 py-1 text-white text-xs font-medium leading-[0.9rem] tracking-[-0.0075rem]',
        gray:
          'h-[1.6875rem] bg-[#DDE1E6] hover:bg-[#c4cbd4] rounded px-3 py-1.5 text-[#697077] text-xs font-medium leading-[0.9375rem]',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'w-full h-[2.5625rem] px-6 py-3',
        sm: 'h-8 rounded-md px-3 text-xs',
        lg: 'h-10 rounded-md px-8',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      // size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
  VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
        type="button"
      />
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
